import { SiShopify } from 'react-icons/si'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import type { ShopifyWebPublisherArgs } from 'sprites-types/workflow/tools/shopifyWebPublisher'
import { shopifyWebPublisher } from 'sprites-types/workflow/tools/shopifyWebPublisher'
import { WebPublisherResult } from 'sprites-types/workflow/tools/webPublisher'
import { BaseWebPublisherDisplay } from '../shared/BaseWebPublisherDisplay'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'
import { ShopifyBlogPostsList } from './ShopifyBlogPostsList'
import { ShopifyPagesList } from './ShopifyPagesList'
import { ShopifyProductsList } from './ShopifyProductsList'

const getActionDisplayName = (action: string) => {
  switch (action) {
    case 'create':
      return 'Create Content'
    case 'update':
      return 'Update Content'
    case 'subagentTaskRunner':
      return 'Natural Language Task'
    case 'getArticle':
      return 'Get Article'
    case 'createArticle':
      return 'Create Article'
    case 'updateArticle':
      return 'Update Article'
    case 'getPage':
      return 'Get Page'
    case 'createPage':
      return 'Create Page'
    case 'updatePage':
      return 'Update Page'
    case 'getProduct':
      return 'Get Product'
    case 'createProduct':
      return 'Create Product'
    case 'updateProduct':
      return 'Update Product'
    case 'listProducts':
      return 'List Products'
    case 'listPages':
      return 'List Pages'
    case 'listBlogPosts':
      return 'List Blog Posts'
    default:
      return action
  }
}

// Helper function to parse structured data from change details
function parseStructuredData(change: { action: string; target: string; details: string }) {
  if (change.action === 'list') {
    try {
      return JSON.parse(change.details)
    } catch {
      return null
    }
  }
  return null
}

// Helper function to extract shop domain from credentials or other sources
function extractShopDomain(nodeExecution: NodeExecution): string | undefined {
  // Try to extract from various possible locations in the execution context
  // This is a best-effort extraction
  const inputs = nodeExecution.inputs
  return inputs?.shopDomain as string | undefined
}

function ShopifyWebPublisherDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const inputs = shopifyWebPublisher.argsSchema.safeParse(nodeExecution.inputs.functionArgs)

  if (!inputs.success) {
    return <SchemaValidationError error={inputs.error} nodeExecution={nodeExecution} />
  }

  const schema = getToolResultSchema(shopifyWebPublisher, inputs.data)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const data = parseResult.data as WebPublisherResult
  const inputData = inputs.data as ShopifyWebPublisherArgs
  const action = inputData.action
  const actionDisplayName = getActionDisplayName(action)
  const shopDomain = extractShopDomain(nodeExecution)

  // Check if this is a listing action with structured data
  if (
    ['listProducts', 'listPages', 'listBlogPosts'].includes(action) &&
    data.changes &&
    data.changes.length > 0
  ) {
    const listingChange = data.changes.find((change) => change.action === 'list')
    if (listingChange) {
      const structuredData = parseStructuredData(listingChange)

      if (structuredData && structuredData.items && Array.isArray(structuredData.items)) {
        // Render specialized listing component based on action
        const ListingContent = () => {
          switch (action) {
            case 'listProducts':
              return (
                <ShopifyProductsList
                  products={structuredData.items}
                  shopDomain={shopDomain}
                  hasNextPage={structuredData.hasNextPage}
                  showCount={true}
                />
              )
            case 'listPages':
              return (
                <ShopifyPagesList
                  pages={structuredData.items}
                  shopDomain={shopDomain}
                  hasNextPage={structuredData.hasNextPage}
                  showCount={true}
                />
              )
            case 'listBlogPosts':
              return (
                <ShopifyBlogPostsList
                  articles={structuredData.items}
                  blogHandle={
                    typeof inputData.args === 'object' &&
                    inputData.args &&
                    'blogHandle' in inputData.args
                      ? (inputData.args.blogHandle as string)
                      : undefined
                  }
                  shopDomain={shopDomain}
                  hasNextPage={structuredData.hasNextPage}
                  showCount={true}
                />
              )
            default:
              return null
          }
        }

        return (
          <BaseWebPublisherDisplay
            data={data}
            platformName="Shopify"
            platformIcon={<SiShopify className="h-4 w-4 text-green-600" />}
            platformColor="text-green-600"
            backgroundColor="bg-green-50"
            actionName={actionDisplayName}
          >
            <div className="mt-3">
              <ListingContent />
            </div>
          </BaseWebPublisherDisplay>
        )
      }
    }
  }

  // Fallback to standard display for non-listing actions or when structured data isn't available
  return (
    <BaseWebPublisherDisplay
      data={data}
      platformName="Shopify"
      platformIcon={<SiShopify className="h-4 w-4 text-green-600" />}
      platformColor="text-green-600"
      backgroundColor="bg-green-50"
      actionName={actionDisplayName}
    />
  )
}

function downloadShopifyWebPublisher(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function shopifyWebPublisherToMarkdown(nodeExecution: NodeExecution): string {
  const inputs = shopifyWebPublisher.argsSchema.safeParse(nodeExecution.inputs.functionArgs)

  if (!inputs.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const schema = getToolResultSchema(shopifyWebPublisher, inputs.data)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const data = parseResult.data as WebPublisherResult
  const inputData = inputs.data as ShopifyWebPublisherArgs
  const action = inputData.action
  const actionDisplayName = getActionDisplayName(action)

  // Check if this is a listing action with structured data
  if (
    ['listProducts', 'listPages', 'listBlogPosts'].includes(action) &&
    data.changes &&
    data.changes.length > 0
  ) {
    const listingChange = data.changes.find((change) => change.action === 'list')
    if (listingChange) {
      const structuredData = parseStructuredData(listingChange)

      if (structuredData && structuredData.items && Array.isArray(structuredData.items)) {
        let itemsList = ''

        if (action === 'listProducts') {
          itemsList = structuredData.items
            .map(
              (product: any) =>
                `- **${product.title}** (${product.handle})\n  - Status: ${product.status}\n  - ID: ${product.id}${product.vendor ? `\n  - Vendor: ${product.vendor}` : ''}${product.productType ? `\n  - Type: ${product.productType}` : ''}`
            )
            .join('\n\n')
        } else if (action === 'listPages') {
          itemsList = structuredData.items
            .map(
              (page: any) =>
                `- **${page.title}** (${page.handle})\n  - Status: ${page.published ? 'Published' : 'Draft'}\n  - ID: ${page.id}${page.publishedAt ? `\n  - Published: ${page.publishedAt}` : ''}`
            )
            .join('\n\n')
        } else if (action === 'listBlogPosts') {
          itemsList = structuredData.items
            .map(
              (article: any) =>
                `- **${article.title}** (${article.handle})\n  - Status: ${article.published ? 'Published' : 'Draft'}\n  - ID: ${article.id}${article.author ? `\n  - Author: ${article.author}` : ''}${article.publishedAt ? `\n  - Published: ${article.publishedAt}` : ''}`
            )
            .join('\n\n')
        }

        return `## Shopify ${actionDisplayName}

**Action:** ${action}

### Results

${data.summary}

${structuredData.hasNextPage ? '**Note:** More items available with pagination.\n' : ''}

### Items Found

${itemsList}

---
`
      }
    }
  }

  // Fallback to standard format
  return `## Shopify ${actionDisplayName}

**Action:** ${action}

### Results

${data.summary}

${data.url ? `**URL:** ${data.url}\n` : ''}
${
  data.changes && data.changes.length > 0
    ? `### Changes Made

${data.changes.map((change) => `- **${change.action}** ${change.target}: ${change.details}`).join('\n')}
`
    : ''
}
---
`
}

export const ShopifyWebPublisherDisplay = createWorkflowToolComponent(
  ShopifyWebPublisherDisplayComponent,
  downloadShopifyWebPublisher,
  shopifyWebPublisherToMarkdown
)
